import { OptionsObject, useSnackbar, VariantType } from 'notistack'
import { ReactNode, useCallback } from 'react'
import { EventType, ToastSubType } from '../../constants'
import { useEventTracking } from '../useEventTracking'
import { CloseButton } from './CloseButton'

type Message = string | ReactNode

type ToastNotifyFunction = (content: Content, variant?: VariantType, overrides?: Partial<OptionsObject>) => string | number

interface Content {
  message: Message
  type: ToastSubType
}

interface ToastEvent {
  type: VariantType
  subtype: ToastSubType
}

interface NotificationContext {
  notify: ToastNotifyFunction
}

const seconds = (count: number) => count * 1000

const defaults: OptionsObject = {
  action: CloseButton,
  anchorOrigin: {
    vertical: 'top',
    horizontal: 'right',
  },
  autoHideDuration: seconds(3),
  persist: false,
}

const useNotification = (options: OptionsObject = {}): NotificationContext => {
  const { enqueueSnackbar } = useSnackbar()
  const { track: trackEvent } = useEventTracking()

  const actualOptions = { ...defaults, ...options }

  const notify = useCallback(
    (content: Content, variant: VariantType = 'info', overrides?: Partial<OptionsObject>) => {
      const data: ToastEvent = {
        subtype: content.type,
        type: variant,
      }

      trackEvent(EventType.TOAST, { data })

      return enqueueSnackbar(content.message, {
        ...actualOptions,
        ...(overrides ?? {}),
        variant,
      })
    },
    [actualOptions, enqueueSnackbar]
  )

  return { notify }
}

export type { ToastNotifyFunction }
export { useNotification }
